
import React, { useState, useEffect } from "react";
import { NavigateOptions, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';

import { useApiService } from "./services/ApiServiceProvider";
import { ConsentStatus } from "./services/ApiService";
import { useKeycloak } from "./services/KeycloakProvider";
import { LoadingSpinner } from "./components/common/LoadingSpinner";

export const ConsentedRoute = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const apiService = useApiService();
  const { userInfo } = useKeycloak();
  const [participantId, setParticipantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [consentStatus, setConsentStatus] = useState<ConsentStatus | null>(null);

  const checkConsentStatus = async (participantId: string) => {
    setLoading(true);

    try {
      const consentInfo = await apiService.getParticipantConsentAndLanguage(participantId);
      if (consentInfo) {
        const language = consentInfo.language || "English";
        if (language) {
          if (language === 'Spanish') {
            i18n.changeLanguage('es-419');
          } else {
            i18n.changeLanguage('en');
          }
        }
        setConsentStatus(consentInfo.consentStatus || 'Pending');

        if (consentInfo.consentStatus === 'Consented') {
          if (consentInfo.isProlific === 'yes') {
            if (!consentInfo.prolificId) {
              navigate('/home');
            }
            else if (consentInfo.showProlificStudyId === 'yes') {
              navigate('/prolific-study-id', { state: { participantId } });
            }
          }
        }
      } else {
        navigate('/error', { state: { message: 'Not found.' } });
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes('Forbidden, you do not have permissions to access this resource')) {
          navigate('/error', { state: { message: `You don't have permissions to view this page` } });
        } else {
          navigate('/error', { state: { message: 'Failed to fetch participant data.' } });
          console.error('Error fetching participant data:', error);
        }
      } else {
        navigate('/error', { state: { message: 'Failed to fetch participant data.' } });
        console.error('Error fetching participant data:', error);
      }
    }

    setLoading(false);
  };

  useEffect(() => {
    if (!participantId) { return; }

    checkConsentStatus(participantId);

  }, [participantId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (consentStatus) {
      if (consentStatus !== 'Consented') {
        console.warn('Consent not given or withdrawn. Redirecting to consent page.');
        navigate(`/consent?status=${consentStatus}`);
      }
    }
  }, [consentStatus]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (userInfo) {
      if (userInfo.subjectId && userInfo.subjectId !== participantId) {
        setParticipantId(userInfo.subjectId);
      }
      else {
        const message = 'No subjectId found in your profile.';
        const navoptions: NavigateOptions = {
          state: { message }
        };

        navigate('/error', navoptions);
      }
    }
  }, [userInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <LoadingSpinner />;
  }

  return <Outlet />;
};
